import {
  PROTOCOL_VERSION,
  type ClientMessage,
  type JoinMode,
  type ServerMessage,
} from "@dogdell/shared";

export function websocketUrl(): string {
  if (import.meta.env.VITE_WS_URL) {
    return import.meta.env.VITE_WS_URL;
  }
  const protocol = location.protocol === "https:" ? "wss:" : "ws:";
  if (import.meta.env.DEV) {
    return `${protocol}//${location.hostname}:2567`;
  }
  return `${protocol}//${location.host}`;
}

type Handler = (message: ServerMessage) => void;

export interface JoinOptions {
  name: string;
  mode?: JoinMode;
  idToken?: string;
  /** Breed slot 0..DOG_BREED_COUNT-1 (Quick play / new pups). */
  color?: number;
}

type PendingIntent =
  | { kind: "spectate" }
  | { kind: "join"; options: JoinOptions };

/**
 * Thin WebSocket client. Opens once for title-screen spectate, then sends
 * `join` on the same socket when the player enters the park.
 */
export class NetworkClient {
  private socket: WebSocket | null = null;
  private readonly handlers = new Set<Handler>();
  private pending: PendingIntent | null = null;
  private intentionalClose = false;

  get connected(): boolean {
    return this.socket?.readyState === WebSocket.OPEN;
  }

  on(handler: Handler): () => void {
    this.handlers.add(handler);
    return () => this.handlers.delete(handler);
  }

  /** Watch the live park without spawning. Safe to call on page load. */
  spectate(): void {
    this.pending = { kind: "spectate" };
    if (this.connected) {
      this.flushPending();
      return;
    }
    this.openSocket();
  }

  /** Spawn into the park (promotes an existing spectator socket when possible). */
  joinPark(options: JoinOptions): void {
    this.pending = { kind: "join", options };
    if (this.connected) {
      this.flushPending();
      return;
    }
    this.openSocket();
  }

  /** @deprecated use joinPark */
  connect(options: JoinOptions): void {
    this.joinPark(options);
  }

  send(message: ClientMessage): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(message));
    }
  }

  close(): void {
    this.intentionalClose = true;
    this.socket?.close();
    this.socket = null;
  }

  private openSocket(): void {
    this.intentionalClose = false;
    const previous = this.socket;
    if (previous && previous.readyState === WebSocket.OPEN) {
      previous.close();
    }
    const url = websocketUrl();
    let opened = false;
    let socket: WebSocket;
    try {
      socket = new WebSocket(url);
    } catch {
      this.emitError(
        `Could not open ${url}. Host the game server and set VITE_WS_URL (wss://…).`,
      );
      return;
    }
    this.socket = socket;

    socket.addEventListener("open", () => {
      opened = true;
      this.flushPending();
    });

    socket.addEventListener("message", (event) => {
      if (typeof event.data !== "string") {
        return;
      }
      try {
        const message = JSON.parse(event.data) as ServerMessage;
        for (const handler of this.handlers) {
          handler(message);
        }
      } catch {
        // ignore malformed frames
      }
    });

    socket.addEventListener("close", () => {
      if (this.socket !== socket) {
        return;
      }
      this.socket = null;
      if (this.intentionalClose) {
        return;
      }
      if (!opened) {
        this.emitError(
          `Park server unreachable at ${url}. Netlify only hosts the website. Deploy the Node server and set VITE_WS_URL.`,
        );
        return;
      }
      this.emitError("Disconnected from the park.");
    });
  }

  private flushPending(): void {
    const pending = this.pending;
    if (!pending || !this.connected) {
      return;
    }
    if (pending.kind === "spectate") {
      this.send({ type: "spectate", protocol: PROTOCOL_VERSION });
      return;
    }
    const { options } = pending;
    this.send({
      type: "join",
      name: options.name,
      protocol: PROTOCOL_VERSION,
      mode: options.mode ?? "guest",
      ...(options.idToken ? { idToken: options.idToken } : {}),
      ...(typeof options.color === "number" ? { color: options.color } : {}),
    });
  }

  private emitError(message: string): void {
    for (const handler of this.handlers) {
      handler({ type: "error", message });
    }
  }
}
